import { useQuery } from "@tanstack/react-query";

import { SATOSPortalNew } from "@/DataAdapter/Portal";
import { SATOSPortal } from "@/DataAdapter/SATOSPortal";

import {
	OmniaDatapointRequest,
	TagNameRequest,
} from "@/model/SATOSLive/Omnia/OmniaDatapointRequest";
import {
	OmniaDatapoint,
	OmniaDatapointResponse,
	OmniaDatapoints,
} from "@/model/SATOSLive/Omnia/OmniaDatapointResponse";
import { TimeseriesResponse } from "@/model/SATOSLive/Omnia/TimeseriesResponse";
import {
	GetTagValueRequest,
	SavedLiveTagResponse,
} from "@/model/SATOSLive/Settings/SavedTagValue";
import { LiveSettingsResponse } from "@/model/SATOSLive/Settings/SettingsResponse";

import { ApplicationQueryKeys } from "@/service/ApplicationQueries";

export const useFetchAllOmniaTimeseries = () => {
	return useQuery<TimeseriesResponse, any>({
		queryKey: [LiveQueryKeys.omnia, "Timeseries"],
		queryFn: async () => {
			const response = await fetch("/api/omnia");

			if (!response.status.toString().startsWith("2")) {
				throw new Error(
					"Server error - Remember to set ClientId and ClientSecret in Azure portal",
				);
			}

			return await response.json();
		},
	});
};

export const useSearchOmniaByName = (search: string) => {
	return useQuery<TimeseriesResponse, any>({
		queryKey: [LiveQueryKeys.omnia, search],
		queryFn: () => SATOSPortal.SearchOmniaByName(search),
		enabled: !!search,
	});
};

export const useFetchLatestDatapointById = (id: string) => {
	return useQuery<OmniaDatapointResponse, any>({
		queryKey: [LiveQueryKeys.omnia, LiveQueryKeys.datapoints, id],
		queryFn: () => SATOSPortal.GetOmniaDatapointById(id),
		enabled: !!id,
		staleTime: 5000,
		refetchInterval: 1500,
	});
};

export const useFetchLatestDatapointByTagName = (tag: string) => {
	return useQuery<OmniaDatapoint[], any>({
		queryKey: [LiveQueryKeys.omnia, LiveQueryKeys.datapoints, tag],
		queryFn: () => SATOSPortal.GetOmniaDatapointByTagName(tag),
		enabled: !!tag,
		staleTime: 5000,
		refetchInterval: 1500,
	});
};

export const useFetchMultipleLatestDatapointsByTagName = (
	request: TagNameRequest[],
) => {
	return useQuery<OmniaDatapoints[], any>({
		queryKey: [
			LiveQueryKeys.omnia,
			LiveQueryKeys.datapoints,
			LiveQueryKeys.multiple,
			request,
		],
		queryFn: () => SATOSPortal.FetchMultipleLatestDatapointsByTagName(request),
		enabled: !!request[0].TagName,
		refetchInterval: 3000,
	});
};

export const useFetchHistoricalDatapoints = (
	request: OmniaDatapointRequest[],
) => {
	return useQuery<OmniaDatapointResponse, any>({
		queryKey: [LiveQueryKeys.omnia, LiveQueryKeys.datapoints, "historic"],
		queryFn: () => SATOSPortal.GetHistoricalDatapoints(request),
		enabled: !!request[0].startTime && !!request[0].endTime,
		refetchOnWindowFocus: false,
	});
};

export const useFetchLiveSettings = () => {
	return useQuery<LiveSettingsResponse, any>({
		queryKey: [
			LiveQueryKeys.live,
			LiveQueryKeys.settings,
			ApplicationQueryKeys.user,
		],
		queryFn: () => SATOSPortalNew.fetchLiveSettings(),
	});
};

export const useFetchSavedTagValueByTag = (request: GetTagValueRequest) => {
	return useQuery<SavedLiveTagResponse, any>({
		queryKey: [LiveQueryKeys.live, LiveQueryKeys.saveTagValue, request.Tag],
		queryFn: () => SATOSPortalNew.fetchSavedTagValueByTag(request),
	});
};

export class LiveQueryKeys {
	static readonly live = "live";
	static readonly omnia = "omnia";
	static readonly timeseries = "Timeseries";
	static readonly settings = "settings";
	static readonly saveTagValue = "saveTagValue";
	static readonly datapoints = "datapoints";
	static readonly multiple = "multiple";
}
